import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "variant"];
import { GYPSUM, OLAF } from 'HubStyleTokens/colors';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import UIIcon from 'UIComponents/icon/UIIcon';
import UILink from 'UIComponents/link/UILink';
import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import memoizeOne from 'react-utils/memoizeOne';
import styled, { css } from 'styled-components';
import UIRouterNavLink from 'ui-addon-react-router-dom/UIRouterNavLink';
import { getPathNames } from '../config/nav/navUtils';
import { LocalizedStatusBadge, getLocalizedBadge } from '../utils/localization';
import { getPrimaryNavItems } from '../config/nav/primaryNavConfig';
import Footer from './Footer';
import { StyledFooter } from '../utils/css';
import NavConfigListItem, { NavConfigListItemLink } from './NavConfigListItem';
import LanguageSwitcher from './header/LanguageSwitcher';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
/**
 * The className applied to active router-aware links in this list.
 * @see https://tools.hubteamqa.com/ui-library/show/ui-addon-react-router-dom/UIRouterNavLink#uirouternavlink/activeClassName
 */
const ACTIVE_LINK_CLASSNAME = 'NestedNavLink-active';

/**
 * Resets the `<ul />` styles, because `UIList` makes a lot of assumptions :(
 */
const resetULStyles = css(["list-style:none;margin:0;padding:0;"]);
const flexSpaceBetweenStyles = css(["display:flex;justify-content:space-between;align-items:center;"]);

/**
 * A branch containing nodes. Clicking drills down into that category or,
 * when `variant="back"`, goes up a level.
 **/
const Branch = styled(_ref => {
  let {
      children,
      variant = 'default'
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  return /*#__PURE__*/_jsxs(NavConfigListItemLink, Object.assign({}, rest, {
    LinkComponent: UILink,
    children: [children, /*#__PURE__*/_jsx(UIIcon, {
      name: variant === 'back' ? 'left' : 'right',
      size: 24,
      className: "m-right-2"
    })]
  }));
}).withConfig({
  displayName: "DrilldownMenu__Branch",
  componentId: "sc-8cexza-0"
})(["font-weight:500;padding:6px 0 6px 13px;background-color:", ";border-radius:", ";", " ", "{margin-left:12px;position:relative;bottom:1px;}"], ({
  variant
}) => variant === 'back' ? GYPSUM : null, ({
  variant
}) => variant === 'back' ? '20px' : null, flexSpaceBetweenStyles, LocalizedStatusBadge);
const BranchUL = styled.ul.withConfig({
  displayName: "DrilldownMenu__BranchUL",
  componentId: "sc-8cexza-1"
})(["", " background-color:", ";position:absolute;top:0;width:100%;left:100%;height:100%;z-index:", ";transform:", ";"], resetULStyles, OLAF, ({
  active,
  depth
}) => active ? depth : null, ({
  active,
  depth
}) => active ? `translate(-${100 * depth}%)` : null);
const UL = styled.ul.withConfig({
  displayName: "DrilldownMenu__UL",
  componentId: "sc-8cexza-2"
})(["", " position:relative;height:100%;li{margin-top:24px;&:first-child{margin-top:0;}}"], resetULStyles);

/**
 * The drilldown `<nav />`.
 */
const DrilldownNav = styled.nav.withConfig({
  displayName: "DrilldownMenu__DrilldownNav",
  componentId: "sc-8cexza-3"
})(["padding:0;position:relative;height:calc(100% - 12px);overflow:hidden;"]);

/**
 * Gets the target ID of this branch link according to its `data-branch-target`
 * attribute.
 */
const getTargetId = ({
  currentTarget: {
    dataset: {
      branchTarget
    }
  }
}) => {
  if (branchTarget) {
    return parseInt(branchTarget, 10);
  }
  return -1;
};

/**
 * The drilldown menu in the header, only displayed at mobile.
 */
const DrilldownMenu = ({
  primaryNavItems = getPrimaryNavItems()
}) => {
  const {
    pathname
  } = useLocation();

  /**
   * Maps each path to a submenu UL to control its visibility.
   */
  const submenuIdMap = useMemo(() => {
    let ulCount = 0;
    return primaryNavItems.reduce((acc, {
      baseDirectory,
      getSecondaryNavConfigs
    }) => {
      ++ulCount;
      acc[baseDirectory] = {
        ulElementId: ulCount
      };
      const parentId = ulCount;
      getSecondaryNavConfigs().forEach(fn => {
        ++ulCount;
        const secondaryNavConfig = fn();
        const {
          commonPath
        } = secondaryNavConfig;
        acc[commonPath] = {
          ulElementId: ulCount,
          parentId
        };
      });
      return acc;
    }, {});
  }, [primaryNavItems]);

  /**
   * An object representing the `ul[data-branch-id]` for a route. Each `<ul />`
   * has an ID (which a `Branch` targets) and a `parentId` representing its parent.
   */

  /**
   * Returns an object representing the `ul[data-branch-id]` for a route. Each
   * `<ul />` has an ID which a `Branch` targets and a `parentId` representing
   * its parent.
   *
   * @returns `{ ulElementId: number; parentId?: number }`
   * @defaults `{ ulElementId: -1 }` (The top level drilldown showing primary nav items)
   */
  const getSubmenuByRoute = memoizeOne((primaryDir, secondaryDir) => {
    const defaultObj = {
      ulElementId: -1
    };
    if (!primaryDir) {
      return defaultObj;
    }
    const absoluteParentPath = primaryDir.startsWith('/') ? primaryDir : `/${primaryDir}`;
    const absoluteCommonPath = secondaryDir ? `${absoluteParentPath}/${secondaryDir}` : absoluteParentPath;

    /**
     * Object mapping to SecondaryNavConfig['commonPath'] if available or,
     * as a fallback, the parent object. If neither are available, map to
     * the top-level menu (-1) which lists all primary nav items.
     */
    const ulObj = submenuIdMap[absoluteCommonPath] || submenuIdMap[absoluteParentPath] || defaultObj;
    return ulObj;
  });

  // Current pathname
  const {
    primaryDir: currPrimaryDir,
    secondaryDir: currSecondaryDir
  } = getPathNames(pathname);
  const {
    ulElementId: currPageUlElementId
  } = getSubmenuByRoute(currPrimaryDir, currSecondaryDir);

  /**
   * The default active branch ID, determined by the primary and secondary
   * directories.
   */
  const [activeBranchId, setActiveBranchId] = useState(currPageUlElementId);
  const mobileNavFooter = /*#__PURE__*/_jsx(StyledFooter, {
    inline: true,
    $width: "article",
    children: /*#__PURE__*/_jsx(LanguageSwitcher, {})
  });
  return /*#__PURE__*/_jsx(DrilldownNav, {
    children: /*#__PURE__*/_jsxs(UL, {
      children: [primaryNavItems.map(({
        label: primaryLabel,
        baseDirectory: primaryDir,
        getSecondaryNavConfigs
      }) => {
        const secondaryNavConfigs = getSecondaryNavConfigs();

        /**
         * When `true`, a list of the available secondary navs should
         * show for this primary nav item, allowing a user to drill
         * down and select from the item's secondary nav menus.
         *
         * @example `true` - Within `API & Reference`, users can drill
         * down into `API Endpoints`, `CMS Reference`, and `UI
         * Components`
         * @example `false` - Within `Getting Started`, there is only
         * one secondary nav config, so showing a submenu of secondary
         * navs is not needed.
         */
        const showMenuOfSecondaryNavs = secondaryNavConfigs.length > 1;
        const {
          ulElementId: primaryUlElementId
        } = getSubmenuByRoute(primaryDir);
        return /*#__PURE__*/_jsxs("li", {
          children: [/*#__PURE__*/_jsx(Branch, {
            "data-test-id": "drilldown-primary-link",
            "data-branch-target": primaryUlElementId,
            onClick: evt => setActiveBranchId(getTargetId(evt)),
            children: primaryLabel
          }), /*#__PURE__*/_jsxs(BranchUL, {
            "data-branch-id": primaryUlElementId,
            "data-branch-active": activeBranchId === primaryUlElementId,
            active: activeBranchId === primaryUlElementId,
            depth: 1,
            children: [/*#__PURE__*/_jsx("li", {
              children: /*#__PURE__*/_jsx(Branch, {
                "data-test-id": "drilldown-back-to-top-level",
                "data-branch-target": -1,
                variant: "back",
                onClick: evt => setActiveBranchId(getTargetId(evt)),
                children: primaryLabel
              })
            }), secondaryNavConfigs.map(fn => {
              const secondaryNavConfig = fn();
              const {
                badge,
                commonPath,
                sectionLabel
              } = secondaryNavConfig;
              const titleNode = badge ? /*#__PURE__*/_jsxs("span", {
                children: [sectionLabel, getLocalizedBadge(badge)]
              }) : sectionLabel;
              const {
                ulElementId: secondaryUlElementId,
                parentId
              } = getSubmenuByRoute(commonPath);
              const hasParent = showMenuOfSecondaryNavs && typeof parentId === 'number';

              /**
               * Required back button node plus optional overview page
               * link.
               */
              const subMenuHeaderNodes = [
              /*#__PURE__*/
              // Secondary nav config - back button pointing to parent or top level, depending if item has more than one secondary nav
              _jsx(Branch, {
                "data-test-id": hasParent ? 'drilldown-back-to-parent' : 'drilldown-back-to-top-level',
                "data-branch-target": hasParent ? parentId : -1,
                variant: "back",
                onClick: evt => setActiveBranchId(getTargetId(evt)),
                children: titleNode
              }, "backLink")];
              if (showMenuOfSecondaryNavs && !secondaryNavConfig.hasMdxOverview) {
                /**
                 * For sections with multiple secondary nav configs, add
                 * overview page link.
                 */
                subMenuHeaderNodes.push( /*#__PURE__*/_jsx(FormattedJSXMessage, {
                  message: "developer-docs-ui.header.primaryNav.overviewLink_jsx",
                  elements: {
                    Link: NavConfigListItemLink
                  },
                  options: {
                    to: commonPath,
                    exact: true,
                    LinkComponent: UIRouterNavLink,
                    activeClassName: ACTIVE_LINK_CLASSNAME
                  }
                }, "indexLink"));
              }
              const listItemNode = /*#__PURE__*/_jsx(NavConfigListItem, {
                navConfig: secondaryNavConfig,
                subMenuFooterNode: /*#__PURE__*/_jsx(Footer, {
                  showGDPRNode: false,
                  additionalContent: /*#__PURE__*/_jsx(LanguageSwitcher, {})
                }),
                subMenuProps: {
                  active: activeBranchId === secondaryUlElementId
                },
                subMenuHeaderNodes: subMenuHeaderNodes
              }, sectionLabel);
              return /*#__PURE__*/_jsxs("li", {
                children: [/*#__PURE__*/_jsx(Branch, {
                  "data-test-id": "drilldown-secondary-nav-title",
                  "data-branch-target": secondaryUlElementId,
                  onClick: evt => setActiveBranchId(getTargetId(evt)),
                  children: titleNode
                }), showMenuOfSecondaryNavs ? /*#__PURE__*/_jsx(BranchUL, {
                  "data-branch-id": secondaryUlElementId,
                  depth: 2,
                  "data-branch-active": activeBranchId === secondaryUlElementId,
                  active: activeBranchId === secondaryUlElementId,
                  children: listItemNode
                }) : /*#__PURE__*/_jsx("ul", {
                  children: listItemNode
                })]
              }, commonPath);
            }), /*#__PURE__*/_jsx("li", {
              children: mobileNavFooter
            })]
          })]
        }, primaryLabel);
      }), /*#__PURE__*/_jsx("li", {
        children: mobileNavFooter
      })]
    })
  });
};
export default DrilldownMenu;